import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  onSnapshot,
  updateDoc,
  getDoc,
  addDoc
} from 'firebase/firestore';
import {
  Camera,
  Monitor,
  Bluetooth,
  Zap,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  StopCircle,
  Wifi,
  Activity,
  Lock,
  Terminal,
  LogOut,
  Mail,
  Key
} from 'lucide-react';

// --- CONFIGURATION ---
const firebaseConfig = {
  apiKey: "AIzaSyCFgS5yc6XbynUde-B_1uWGelOV9bDG1Tg",
  authDomain: "madscienceschule.firebaseapp.com",
  projectId: "madscienceschule",
  storageBucket: "madscienceschule.firebasestorage.app",
  messagingSenderId: "645623470948",
  appId: "1:645623470948:web:d640bfb39e74c005733871"
};
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Google STUN servers for NAT traversal
const rtcConfig = {
  iceServers: [
    { urls: ['stun:stun1.l.google.com:19302', 'stun:stun2.l.google.com:19302'] },
  ],
};

// Nordic UART Service (Standard for Micro:bit)
const UART_SERVICE_UUID = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const UART_TX_CHARACTERISTIC_UUID = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';

export default function TelepresenceSystem() {
  // State
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState('landing'); // 'landing', 'camera', 'controller'
  const [roomId, setRoomId] = useState('');
  const [status, setStatus] = useState('SYSTEM IDLE');
  const [bleConnected, setBleConnected] = useState(false);
  const [logs, setLogs] = useState([]);
  const [wakeLock, setWakeLock] = useState(null);

  // Auth State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [authError, setAuthError] = useState('');

  // Refs (Mutable, non-rendering)
  const peerConnection = useRef(null);
  const localStream = useRef(null);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  const bleDevice = useRef(null);
  const bleCharacteristic = useRef(null);

  // --- LOGGING UTILITY ---
  const addLog = (msg) => {
    console.log(msg);
    setLogs(prev => [`[${new Date().toLocaleTimeString().split(' ')[0]}] ${msg}`, ...prev.slice(0, 4)]);
  };

  // --- AUTHENTICATION ---
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      if (u) {
        setUser(u);
        addLog(`Identity Verified: ${u.email || u.uid.slice(0, 5)}`);
      } else {
        setUser(null);
      }
    });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setAuthError(err.message);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setAuthError(err.message);
    }
  };

  const handleGoogleLogin = async () => {
    setAuthError('');
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      setAuthError(err.message);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setMode('landing');
      setLogs([]);
    } catch (err) {
      console.error(err);
    }
  };

  // --- WAKE LOCK ---
  const requestWakeLock = async () => {
    if ('wakeLock' in navigator) {
      try {
        const lock = await navigator.wakeLock.request('screen');
        setWakeLock(lock);
        addLog('Screen Wake Lock Active');
      } catch (err) {
        addLog(`Wake Lock Failed: ${err.name}`);
      }
    }
  };

  // --- WEBRTC CORE ---
  const createPeerConnection = (id, candidateCollectionName) => {
    const pc = new RTCPeerConnection(rtcConfig);

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const candidatesCol = collection(db, 'artifacts', firebaseConfig.appId, 'public', 'data', `calls/${id}/${candidateCollectionName}`);
        addDoc(candidatesCol, event.candidate.toJSON());
      }
    };

    pc.onconnectionstatechange = () => {
      setStatus(`LINK STATUS: ${pc.connectionState.toUpperCase()}`);
      if (pc.connectionState === 'connected') addLog('P2P Link Established');
      if (pc.connectionState === 'disconnected') addLog('P2P Link Lost');
    };

    return pc;
  };

  // --- CAMERA MODE ---
  const startCameraMode = async () => {
    const id = roomId || Math.random().toString(36).substring(2, 8).toUpperCase();
    setRoomId(id);
    setMode('camera');
    requestWakeLock();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', height: { ideal: 720 } },
        audio: false
      });

      localStream.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      const pc = createPeerConnection(id, 'candidates');
      peerConnection.current = pc;
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      const callDocRef = doc(db, 'artifacts', firebaseConfig.appId, 'public', 'data', `calls/${id}`);
      const offerDescription = await pc.createOffer();
      await pc.setLocalDescription(offerDescription);

      const offer = { sdp: offerDescription.sdp, type: offerDescription.type };
      await setDoc(callDocRef, { offer });
      addLog(`Signaling Channel Open: ${id}`);

      onSnapshot(callDocRef, (snapshot) => {
        const data = snapshot.data();
        if (!pc.currentRemoteDescription && data?.answer) {
          const answerDescription = new RTCSessionDescription(data.answer);
          pc.setRemoteDescription(answerDescription);
          addLog('Handshake Complete');
        }
      });

      const answerCandidates = collection(db, 'artifacts', firebaseConfig.appId, 'public', 'data', `calls/${id}/answerCandidates`);
      onSnapshot(answerCandidates, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            pc.addIceCandidate(new RTCIceCandidate(change.doc.data()));
          }
        });
      });

    } catch (err) {
      addLog(`Camera Fail: ${err.message}`);
    }
  };

  // --- CONTROLLER MODE ---
  const startControllerMode = async () => {
    if (!roomId) return alert('Missing Session ID');
    setMode('controller');
    addLog(`Connecting to Node: ${roomId}`);

    const pc = createPeerConnection(roomId, 'answerCandidates');
    peerConnection.current = pc;

    pc.ontrack = (event) => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
        addLog('Video Feed Acquired');
      }
    };

    const callDocRef = doc(db, 'artifacts', firebaseConfig.appId, 'public', 'data', `calls/${roomId}`);

    // Use onSnapshot for resilience (waits for connection instead of failing immediately)
    onSnapshot(callDocRef, async (snapshot) => {
      const data = snapshot.data();
      // If we are already connected or there is no offer yet, do nothing
      if (pc.currentRemoteDescription || !data?.offer) return;

      addLog('Offer Received');
      await pc.setRemoteDescription(new RTCSessionDescription(data.offer));

      const answerDescription = await pc.createAnswer();
      await pc.setLocalDescription(answerDescription);

      const answer = { type: answerDescription.type, sdp: answerDescription.sdp };
      await updateDoc(callDocRef, { answer });

      const candidatesCol = collection(db, 'artifacts', firebaseConfig.appId, 'public', 'data', `calls/${roomId}/candidates`);
      onSnapshot(candidatesCol, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            pc.addIceCandidate(new RTCIceCandidate(change.doc.data()));
          }
        });
      });
    });
  };

  // --- BLUETOOTH ---
  const connectBluetooth = async () => {
    if (!navigator.bluetooth) {
      alert("Web Bluetooth is not supported in this browser.\nPlease use Google Chrome or Microsoft Edge on desktop.");
      return;
    }

    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: 'BBC micro:bit' }],
        optionalServices: [UART_SERVICE_UUID]
      });

      bleDevice.current = device;
      device.addEventListener('gattserverdisconnected', () => {
        setBleConnected(false);
        addLog('BLE Link Severed');
      });

      const server = await device.gatt.connect();
      const service = await server.getPrimaryService(UART_SERVICE_UUID);
      bleCharacteristic.current = await service.getCharacteristic(UART_TX_CHARACTERISTIC_UUID);

      setBleConnected(true);
      addLog('BLE Link Established (UART)');
    } catch (err) {
      addLog(`BLE Error: ${err.message}`);
    }
  };

  const sendCommand = async (cmd) => {
    if (!bleConnected || !bleCharacteristic.current) return;
    try {
      const encoder = new TextEncoder();
      await bleCharacteristic.current.writeValue(encoder.encode(cmd + '\n'));
    } catch (err) {
      console.warn('BLE Write Fail', err);
    }
  };

  useEffect(() => {
    if (mode !== 'controller') return;

    const handleKeyDown = (e) => {
      if (e.repeat) return;
      const map = { ArrowUp: 'F', ArrowDown: 'B', ArrowLeft: 'L', ArrowRight: 'R', ' ': 'S' };
      if (map[e.key]) sendCommand(map[e.key]);
    };

    const handleKeyUp = (e) => {
      const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (keys.includes(e.key)) sendCommand('S');
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [mode, bleConnected]);

  // --- UI RENDER ---
  if (!user) return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center text-[#E0E0E0] font-sans selection:bg-[#00F3FF] selection:text-black">
      {/* --- CSS INJECTION FOR AESTHETICS --- */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;700;900&family=Fira+Code:wght@300;400;600&display=swap');
        .font-cyber { font-family: 'Orbitron', sans-serif; }
        .font-mono { font-family: 'Fira Code', monospace; }
        .bg-alien-grid {
          background-size: 40px 40px;
          background-image:
            linear-gradient(to right, rgba(0, 243, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0, 243, 255, 0.05) 1px, transparent 1px);
        }
        .glow-border { box-shadow: 0 0 10px rgba(0, 243, 255, 0.1), inset 0 0 5px rgba(0, 243, 255, 0.1); }
      `}</style>

      <div className="absolute inset-0 bg-alien-grid pointer-events-none" />

      <div className="w-full max-w-md p-8 border border-[#00F3FF]/30 bg-[#121212]/90 backdrop-blur relative z-10">
        <div className="absolute -top-3 left-4 bg-[#121212] px-2 font-mono text-xs text-[#00F3FF]">SECURE ACCESS REQUIRED</div>

        <div className="text-center mb-8">
          <Zap className="w-10 h-10 text-[#00F3FF] mx-auto mb-4" />
          <h1 className="text-2xl font-cyber font-bold tracking-widest">Tele<span className="text-[#00F3FF]">Link</span></h1>
          <p className="text-[#39FF14] font-mono text-[10px] tracking-[0.3em] mt-2">AUTHENTICATION GATEWAY</p>
        </div>

        {authError && (
          <div className="mb-6 p-3 border border-red-500/50 bg-red-900/10 text-red-400 text-xs font-mono">
            ERROR: {authError}
          </div>
        )}

        <form onSubmit={isRegistering ? handleSignup : handleLogin} className="space-y-4">
          <div className="relative group">
            <Mail className="absolute left-4 top-3 w-5 h-5 text-[#00F3FF]/50 group-focus-within:text-[#00F3FF] transition-colors" />
            <input
              type="email"
              placeholder="OPERATOR ID (EMAIL)"
              className="w-full bg-black border border-[#333] focus:border-[#00F3FF] pl-12 p-3 text-[#E0E0E0] placeholder-[#333] outline-none font-mono text-xs transition-colors"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="relative group">
            <Key className="absolute left-4 top-3 w-5 h-5 text-[#00F3FF]/50 group-focus-within:text-[#00F3FF] transition-colors" />
            <input
              type="password"
              placeholder="ACCESS CODE (PASSWORD)"
              className="w-full bg-black border border-[#333] focus:border-[#00F3FF] pl-12 p-3 text-[#E0E0E0] placeholder-[#333] outline-none font-mono text-xs transition-colors"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-[#00F3FF]/10 border border-[#00F3FF] text-[#00F3FF] hover:bg-[#00F3FF] hover:text-black font-cyber font-bold tracking-wider transition-all"
          >
            {isRegistering ? 'INITIALIZE NEW ID' : 'AUTHENTICATE'}
          </button>
        </form>

        <div className="mt-6 flex items-center justify-between gap-4">
          <div className="h-[1px] bg-[#333] flex-1" />
          <span className="text-[#333] font-mono text-[10px]">OR</span>
          <div className="h-[1px] bg-[#333] flex-1" />
        </div>

        <button
          onClick={handleGoogleLogin}
          className="mt-6 w-full py-3 border border-[#333] hover:border-[#E0E0E0] text-[#888] hover:text-[#E0E0E0] font-mono text-xs transition-all flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z" /></svg>
          ACCESS VIA GOOGLE
        </button>

        <div className="mt-6 text-center">
          <button
            onClick={() => setIsRegistering(!isRegistering)}
            className="text-[#39FF14] hover:text-[#00F3FF] font-mono text-[10px] underline decoration-dotted underline-offset-4 transition-colors"
          >
            {isRegistering ? 'ALREADY HAVE AN ID? LOGIN' : 'REQUEST NEW CREDENTIALS'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-[#E0E0E0] font-sans selection:bg-[#00F3FF] selection:text-black overflow-hidden">
      {/* --- CSS INJECTION FOR AESTHETICS --- */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;700;900&family=Fira+Code:wght@300;400;600&display=swap');
        
        .font-cyber { font-family: 'Orbitron', sans-serif; }
        .font-mono { font-family: 'Fira Code', monospace; }
        
        .bg-alien-grid {
          background-size: 40px 40px;
          background-image: 
            linear-gradient(to right, rgba(0, 243, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0, 243, 255, 0.05) 1px, transparent 1px);
        }
        
        .scanline {
          background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0) 50%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.2));
          background-size: 100% 4px;
          animation: scan 10s linear infinite;
          pointer-events: none;
        }
        
        .glow-border {
          box-shadow: 0 0 10px rgba(0, 243, 255, 0.1), inset 0 0 5px rgba(0, 243, 255, 0.1);
        }
        
        @keyframes scan { from { background-position: 0 0; } to { background-position: 0 100%; } }
      `}</style>

      {/* --- LANDING --- */}
      {mode === 'landing' && (
        <div className="h-screen bg-alien-grid flex flex-col items-center justify-center p-4 relative">
          {/* Decorative Corners */}
          <div className="absolute top-6 left-6 w-16 h-16 border-t-2 border-l-2 border-[#00F3FF]" />
          <div className="absolute top-6 right-6 w-16 h-16 border-t-2 border-r-2 border-[#00F3FF]" />
          <div className="absolute bottom-6 left-6 w-16 h-16 border-b-2 border-l-2 border-[#00F3FF]" />
          <div className="absolute bottom-6 right-6 w-16 h-16 border-b-2 border-r-2 border-[#00F3FF]" />

          <div className="max-w-md w-full space-y-8 z-10">
            <div className="text-center space-y-2 border-b border-[#39FF14]/30 pb-8 relative">
              <button
                onClick={handleLogout}
                className="absolute top-0 right-0 p-2 text-[#00F3FF] hover:text-[#39FF14] transition-colors"
                title="Disconnect Identity"
              >
                <LogOut className="w-5 h-5" />
              </button>
              <div className="flex justify-center mb-4">
                <div className="p-4 border border-[#00F3FF] bg-[#00F3FF]/10 glow-border">
                  <Zap className="w-8 h-8 text-[#00F3FF]" />
                </div>
              </div>
              <h1 className="text-4xl font-cyber font-bold text-[#E0E0E0] tracking-widest uppercase">
                Tele<span className="text-[#00F3FF]">Link</span>
              </h1>
              <p className="text-[#39FF14] font-mono text-xs tracking-[0.3em]">SECURE ROBOTICS UPLINK V2.4</p>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <button
                onClick={startCameraMode}
                className="group relative p-6 border border-[#00F3FF]/50 hover:border-[#39FF14] bg-[#121212] hover:bg-[#1a1a1a] transition-all duration-300 overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-1 h-full bg-[#00F3FF] group-hover:bg-[#39FF14] transition-colors" />
                <div className="flex items-center gap-6">
                  <Camera className="w-8 h-8 text-[#00F3FF] group-hover:text-[#39FF14]" />
                  <div className="text-left">
                    <div className="font-cyber font-bold text-xl group-hover:text-[#39FF14] transition-colors">TRANSMITTER</div>
                    <div className="font-mono text-[10px] text-slate-500 uppercase">Role: Camera Node (iPhone)</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setMode('input_id')}
                className="group relative p-6 border border-[#FF00FF]/50 hover:border-[#FF00FF] bg-[#121212] hover:bg-[#1a1a1a] transition-all duration-300 overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-1 h-full bg-[#FF00FF] group-hover:bg-[#FF00FF] transition-colors" />
                <div className="flex items-center gap-6">
                  <Terminal className="w-8 h-8 text-[#FF00FF] group-hover:text-[#FF00FF]" />
                  <div className="text-left">
                    <div className="font-cyber font-bold text-xl group-hover:text-[#FF00FF] transition-colors">OPERATOR</div>
                    <div className="font-mono text-[10px] text-slate-500 uppercase">Role: Controller (PC)</div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- INPUT ID --- */}
      {mode === 'input_id' && (
        <div className="h-screen bg-alien-grid flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-md p-8 border border-[#00F3FF] bg-[#121212] relative">
            <div className="absolute -top-3 left-4 bg-[#121212] px-2 font-mono text-xs text-[#00F3FF]">AUTHENTICATION KEY</div>

            <div className="space-y-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="XXXXXX"
                  className="w-full bg-black border border-[#333] focus:border-[#39FF14] p-4 text-center text-3xl font-mono tracking-[0.5em] text-[#39FF14] placeholder-[#333] outline-none uppercase transition-colors"
                  onChange={(e) => setRoomId(e.target.value.toUpperCase())}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setMode('landing')}
                  className="py-3 border border-red-900 text-red-500 hover:bg-red-900/20 font-mono text-xs hover:text-red-400 transition-colors"
                >
                  ABORT
                </button>
                <button
                  onClick={startControllerMode}
                  className="py-3 bg-[#00F3FF]/10 border border-[#00F3FF] text-[#00F3FF] hover:bg-[#00F3FF] hover:text-black font-mono text-xs font-bold transition-all"
                >
                  INITIATE LINK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- ACTIVE INTERFACE --- */}
      {(mode === 'camera' || mode === 'controller') && (
        <div className="relative h-screen w-screen overflow-hidden bg-black flex flex-col">
          {/* Scanline Overlay */}
          <div className="absolute inset-0 scanline z-20 opacity-30 pointer-events-none" />

          {/* HUD: Top Bar */}
          <div className="absolute top-0 w-full z-30 p-4 flex justify-between items-start bg-gradient-to-b from-[#050505] to-transparent">
            <div className="flex items-center gap-4">
              <div className="border border-[#00F3FF] p-2 bg-black/50 backdrop-blur">
                <Lock className="w-4 h-4 text-[#00F3FF]" />
              </div>
              <div>
                <div className="font-mono text-[10px] text-[#00F3FF] opacity-70 uppercase tracking-wider">Encryption Key</div>
                <div className="font-cyber text-xl text-white tracking-widest">{roomId}</div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className={`px-3 py-1 border font-mono text-[10px] font-bold uppercase tracking-wide flex items-center gap-2 ${status.includes('CONNECTED') ? 'border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10' : 'border-amber-500 text-amber-500 bg-amber-500/10'}`}>
                <Wifi className="w-3 h-3" />
                {status}
              </div>

              {mode === 'controller' && (
                <button
                  onClick={connectBluetooth}
                  className={`px-3 py-1 border font-mono text-[10px] font-bold uppercase tracking-wide flex items-center gap-2 transition-all ${bleConnected ? 'border-[#00F3FF] text-black bg-[#00F3FF]' : 'border-[#333] text-[#E0E0E0] hover:border-[#00F3FF] hover:text-[#00F3FF] bg-black'}`}
                >
                  <Bluetooth className="w-3 h-3" />
                  {bleConnected ? 'UART ONLINE' : 'LINK UART'}
                </button>
              )}
            </div>
          </div>

          {/* HUD: Corners */}
          <div className="absolute top-4 left-4 w-32 h-32 border-t border-l border-[#00F3FF]/30 z-20 pointer-events-none" />
          <div className="absolute top-4 right-4 w-32 h-32 border-t border-r border-[#00F3FF]/30 z-20 pointer-events-none" />
          <div className="absolute bottom-4 left-4 w-32 h-32 border-b border-l border-[#00F3FF]/30 z-20 pointer-events-none" />
          <div className="absolute bottom-4 right-4 w-32 h-32 border-b border-r border-[#00F3FF]/30 z-20 pointer-events-none" />

          {/* HUD: Crosshair */}
          {mode === 'controller' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20 opacity-20">
              <div className="w-[300px] h-[300px] border border-[#39FF14] rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-[#39FF14]" />
              </div>
              <div className="absolute w-full h-[1px] bg-[#39FF14]/30" />
              <div className="absolute h-full w-[1px] bg-[#39FF14]/30" />
            </div>
          )}

          {/* Video Feed */}
          <div className="flex-1 bg-[#0a0a0a] relative flex items-center justify-center z-10">
            {mode === 'camera' ? (
              <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover opacity-80" />
            ) : (
              <video ref={remoteVideoRef} autoPlay playsInline className="max-w-full max-h-full object-contain opacity-90" />
            )}
          </div>

          {/* HUD: Logs */}
          <div className="absolute bottom-8 left-8 z-30 font-mono text-[10px] text-[#39FF14] pointer-events-none flex flex-col-reverse h-32 w-64 mask-image-gradient">
            {logs.map((log, i) => (
              <div key={i} className="bg-black/60 px-2 py-1 mb-1 border-l-2 border-[#39FF14]">
                <span className="opacity-50 mr-2">{'>'}</span>
                {log}
              </div>
            ))}
          </div>

          {/* HUD: Controls */}
          {mode === 'controller' && (
            <div className="absolute bottom-8 right-8 z-30 pointer-events-none">
              <div className="grid grid-cols-3 gap-2 opacity-80">
                <div />
                <div className="w-12 h-12 border border-[#00F3FF] bg-[#00F3FF]/10 flex items-center justify-center shadow-[0_0_10px_rgba(0,243,255,0.2)]"><ArrowUp className="w-6 h-6 text-[#00F3FF]" /></div>
                <div />
                <div className="w-12 h-12 border border-[#00F3FF] bg-[#00F3FF]/10 flex items-center justify-center shadow-[0_0_10px_rgba(0,243,255,0.2)]"><ArrowLeft className="w-6 h-6 text-[#00F3FF]" /></div>
                <div className="w-12 h-12 border border-[#00F3FF] bg-[#00F3FF]/10 flex items-center justify-center shadow-[0_0_10px_rgba(0,243,255,0.2)]"><ArrowDown className="w-6 h-6 text-[#00F3FF]" /></div>
                <div className="w-12 h-12 border border-[#00F3FF] bg-[#00F3FF]/10 flex items-center justify-center shadow-[0_0_10px_rgba(0,243,255,0.2)]"><ArrowRight className="w-6 h-6 text-[#00F3FF]" /></div>
              </div>
              <div className="mt-4 text-center font-mono text-[9px] text-[#00F3FF] tracking-widest uppercase">Manual Override Active</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}